import { createApi } from '@reduxjs/toolkit/query/react';
import { isRequiredDocumentType } from '@shared/property/documents';

import type {
  ActivityLogEntry,
  PropertyDocumentRow,
  PropertyOwnerRow,
  PropertyPhotoRow,
  PropertyRow,
  PropertyVideoRow,
  PropertyYoutubeLinkRow,
  StatusHistoryEntry
} from '@/types/property';
import { coercePropertyStatus } from '@/types/property';

import { apiBaseUrl } from '../Api';
import { makeBaseQuery } from '../baseQuery';
import type {
  AddYoutubeLinkArg,
  CreatePropertyPayload,
  PropertyDetailResponse,
  PropertyIdArg,
  PropertyMutationResult,
  PropertyScopedArg,
  PropertyTimelineResult,
  RemovePropertyDocumentArg,
  RemovePropertyPhotoArg,
  RemovePropertyVideoArg,
  RemoveYoutubeLinkArg,
  ReuploadedSellerDocument,
  ReuploadSellerDocumentArg,
  UpdatePropertyArg,
  UploadedPropertyDocument,
  UploadPropertyDocumentArg,
  UploadPropertyPhotosArg,
  UploadPropertyVideosArg
} from './propertyTypes';

/**
 * Seller-owned properties and their documents: `/api/properties/*` (CRUD + document upload)
 * and `/api/seller/properties/*` (the detail page's read model). Both prefixes sit under
 * `/api`, so this service uses `apiBaseUrl` — only `/auth` and `/user` need the root.
 *
 * Every normalizer below is module-scope, takes `unknown` and is TOTAL: never throws, never
 * returns `undefined`. A throwing normalizer takes out the route's error boundary instead of
 * letting the query surface its own error state.
 */
const asRecord = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

/** The `{ success, message, data }` envelope, stripped. A bare payload passes through. */
const envelope = (response: unknown): unknown => {
  const root = asRecord(response);
  return 'data' in root ? root.data : response;
};

const asArray = <T>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);

const asString = (value: unknown, fallback = ''): string =>
  typeof value === 'string' ? value : fallback;

const asNumber = (value: unknown, fallback = 0): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
    return Number(value);
  }
  return fallback;
};

const asNullableNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/** A row is passed through as-is once it looks like an object — we never invent field values. */
const asRow = <T>(value: unknown): T | null => {
  const raw = asRecord(value);
  return Object.keys(raw).length > 0 ? (raw as T) : null;
};

export function normalizePropertyList(response: unknown): PropertyRow[] {
  return asArray<PropertyRow>(envelope(response));
}

export function normalizePropertyDetail(response: unknown): PropertyDetailResponse {
  const data = asRecord(envelope(response));
  return {
    property: asRow<PropertyRow>(data.property),
    owners: asArray<PropertyOwnerRow>(data.owners),
    documents: asArray<PropertyDocumentRow>(data.documents),
    photos: asArray<PropertyPhotoRow>(data.photos),
    videos: asArray<PropertyVideoRow>(data.videos),
    youtubeLinks: asArray<PropertyYoutubeLinkRow>(data.youtubeLinks)
  };
}

export function normalizePropertyPhotos(response: unknown): PropertyPhotoRow[] {
  return asArray<PropertyPhotoRow>(envelope(response));
}

export function normalizePropertyVideos(response: unknown): PropertyVideoRow[] {
  return asArray<PropertyVideoRow>(envelope(response));
}

export function normalizeYoutubeLink(response: unknown): PropertyYoutubeLinkRow {
  return asRecord(envelope(response)) as unknown as PropertyYoutubeLinkRow;
}

export function normalizeDocuments(response: unknown): PropertyDocumentRow[] {
  const data = envelope(response);
  if (Array.isArray(data)) return data as PropertyDocumentRow[];
  return asArray<PropertyDocumentRow>(asRecord(data).documents);
}

export function normalizeActivity(response: unknown): ActivityLogEntry[] {
  const data = envelope(response);
  if (Array.isArray(data)) return data as ActivityLogEntry[];
  return asArray<ActivityLogEntry>(asRecord(data).items);
}

/**
 * `currentStatus` is run through `coercePropertyStatus` exactly as the axios service did —
 * the backend still answers the legacy `verified` for some rows, and the stepper would
 * silently render no step at all for a status outside the flow.
 */
export function normalizeTimeline(response: unknown): PropertyTimelineResult {
  const data = asRecord(envelope(response));
  return {
    history: asArray<StatusHistoryEntry>(data.history),
    currentStatus: coercePropertyStatus(data.currentStatus),
    assignedLawyerId: asNullableNumber(data.assignedLawyerId),
    assignedLawyerName: typeof data.assignedLawyerName === 'string' ? data.assignedLawyerName : null
  };
}

/** `id: 0` is a deterministic sentinel, not an invented id — it never travels back to the API. */
export function normalizeMutationResult(response: unknown): PropertyMutationResult {
  return { id: asNumber(asRecord(envelope(response)).id) };
}

export function normalizeUploadedDocument(response: unknown): UploadedPropertyDocument {
  const data = asRecord(envelope(response));
  return {
    id: asNumber(data.id),
    fileName: asString(data.fileName),
    fileUrl: asString(data.fileUrl),
    fileSize: asNumber(data.fileSize),
    documentType: asString(data.documentType, 'other')
  };
}

export function normalizeReuploadedDocument(response: unknown): ReuploadedSellerDocument {
  const data = asRecord(envelope(response));
  const version = asNullableNumber(data.version);
  return {
    ...normalizeUploadedDocument(response),
    ...(version === null ? {} : { version })
  };
}

// NOTE: RTK delays invalidation until every in-flight upload settles, so patch the cache.
export function addUploadedDocument(
  detail: PropertyDetailResponse,
  arg: UploadPropertyDocumentArg,
  uploaded: UploadedPropertyDocument
): void {
  if (detail.documents.some((d) => d.id === uploaded.id)) return;
  detail.documents.push({
    id: uploaded.id,
    property_id: arg.propertyId,
    document_type: arg.documentType,
    file_name: uploaded.fileName,
    file_url: uploaded.fileUrl,
    file_size: uploaded.fileSize,
    is_required: isRequiredDocumentType(arg.documentType) ? 1 : 0
  });
}

export function dropDocument(detail: PropertyDetailResponse, docId: number): void {
  detail.documents = detail.documents.filter((d) => d.id !== docId);
}

export const propertyApi = createApi({
  reducerPath: 'propertyApi',
  baseQuery: makeBaseQuery(apiBaseUrl),
  tagTypes: [
    'Property',
    'PropertyDocument',
    'PropertyPhoto',
    'PropertyVideo',
    'PropertyYoutubeLink',
    'PropertyTimeline',
    'PropertyActivity'
  ],
  endpoints: (builder) => ({
    listProperties: builder.query<PropertyRow[], void>({
      query: () => ({ url: 'properties' }),
      transformResponse: (response: unknown) => normalizePropertyList(response),
      providesTags: [{ type: 'Property', id: 'LIST' }]
    }),
    getProperty: builder.query<PropertyDetailResponse, PropertyIdArg>({
      query: ({ id }) => ({ url: `properties/${id}` }),
      transformResponse: (response: unknown) => normalizePropertyDetail(response),
      providesTags: (_result, _error, { id }) => [
        { type: 'Property', id: String(id) },
        { type: 'PropertyDocument', id: String(id) },
        { type: 'PropertyPhoto', id: String(id) },
        { type: 'PropertyVideo', id: String(id) },
        { type: 'PropertyYoutubeLink', id: String(id) }
      ]
    }),
    createProperty: builder.mutation<PropertyMutationResult, CreatePropertyPayload>({
      query: (body) => ({ url: 'properties', method: 'POST', body }),
      transformResponse: (response: unknown) => normalizeMutationResult(response),
      invalidatesTags: [{ type: 'Property', id: 'LIST' }]
    }),
    updateProperty: builder.mutation<PropertyMutationResult, UpdatePropertyArg>({
      query: ({ id, body }) => ({ url: `properties/${id}`, method: 'PUT', body }),
      transformResponse: (response: unknown) => normalizeMutationResult(response),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Property', id: String(id) },
        { type: 'Property', id: 'LIST' },
        { type: 'PropertyTimeline', id: String(id) },
        { type: 'PropertyActivity', id: String(id) }
      ]
    }),
    deleteProperty: builder.mutation<void, PropertyIdArg>({
      query: ({ id }) => ({ url: `properties/${id}`, method: 'DELETE' }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Property', id: String(id) },
        { type: 'Property', id: 'LIST' }
      ]
    }),
    /**
     * FormData body, deliberately with NO `Content-Type` header: the browser has to set
     * `multipart/form-data; boundary=…` itself, and a hand-set header loses the boundary
     * so multer parses zero fields.
     */
    uploadPropertyDocument: builder.mutation<UploadedPropertyDocument, UploadPropertyDocumentArg>({
      query: ({ propertyId, file, documentType }) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('documentType', documentType);
        return { url: `properties/${propertyId}/documents`, method: 'POST', body: formData };
      },
      transformResponse: (response: unknown) => normalizeUploadedDocument(response),
      invalidatesTags: (_result, _error, { propertyId }) => [
        { type: 'PropertyDocument', id: String(propertyId) },
        { type: 'Property', id: String(propertyId) }
      ],
      onQueryStarted: async (arg, { dispatch, queryFulfilled }): Promise<void> => {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            propertyApi.util.updateQueryData('getProperty', { id: arg.propertyId }, (draft) =>
              addUploadedDocument(draft, arg, data)
            )
          );
        } catch {
          /* a failed upload changed nothing */
        }
      }
    }),
    removePropertyDocument: builder.mutation<void, RemovePropertyDocumentArg>({
      query: ({ propertyId, docId }) => ({
        url: `properties/${propertyId}/documents/${docId}`,
        method: 'DELETE'
      }),
      invalidatesTags: (_result, _error, { propertyId }) => [
        { type: 'PropertyDocument', id: String(propertyId) },
        { type: 'Property', id: String(propertyId) }
      ],
      onQueryStarted: async (
        { propertyId, docId },
        { dispatch, queryFulfilled }
      ): Promise<void> => {
        try {
          await queryFulfilled;
          dispatch(
            propertyApi.util.updateQueryData('getProperty', { id: propertyId }, (draft) =>
              dropDocument(draft, docId)
            )
          );
        } catch {
          /* a failed remove changed nothing */
        }
      }
    }),
    uploadPropertyPhotos: builder.mutation<PropertyPhotoRow[], UploadPropertyPhotosArg>({
      query: ({ propertyId, files }) => {
        const formData = new FormData();
        for (const file of files) formData.append('photos', file);
        return { url: `properties/${propertyId}/photos`, method: 'POST', body: formData };
      },
      transformResponse: (response: unknown) => normalizePropertyPhotos(response),
      invalidatesTags: (_result, _error, { propertyId }) => [
        { type: 'PropertyPhoto', id: String(propertyId) },
        { type: 'Property', id: String(propertyId) }
      ]
    }),
    removePropertyPhoto: builder.mutation<void, RemovePropertyPhotoArg>({
      query: ({ propertyId, photoId }) => ({
        url: `properties/${propertyId}/photos/${photoId}`,
        method: 'DELETE'
      }),
      invalidatesTags: (_result, _error, { propertyId }) => [
        { type: 'PropertyPhoto', id: String(propertyId) },
        { type: 'Property', id: String(propertyId) }
      ]
    }),
    uploadPropertyVideos: builder.mutation<PropertyVideoRow[], UploadPropertyVideosArg>({
      query: ({ propertyId, files }) => {
        const formData = new FormData();
        for (const file of files) formData.append('videos', file);
        return { url: `properties/${propertyId}/videos`, method: 'POST', body: formData };
      },
      transformResponse: (response: unknown) => normalizePropertyVideos(response),
      invalidatesTags: (_result, _error, { propertyId }) => [
        { type: 'PropertyVideo', id: String(propertyId) },
        { type: 'Property', id: String(propertyId) }
      ]
    }),
    removePropertyVideo: builder.mutation<void, RemovePropertyVideoArg>({
      query: ({ propertyId, videoId }) => ({
        url: `properties/${propertyId}/videos/${videoId}`,
        method: 'DELETE'
      }),
      invalidatesTags: (_result, _error, { propertyId }) => [
        { type: 'PropertyVideo', id: String(propertyId) },
        { type: 'Property', id: String(propertyId) }
      ]
    }),
    addYoutubeLink: builder.mutation<PropertyYoutubeLinkRow, AddYoutubeLinkArg>({
      query: ({ propertyId, url }) => ({
        url: `properties/${propertyId}/youtube-links`,
        method: 'POST',
        body: { url }
      }),
      transformResponse: (response: unknown) => normalizeYoutubeLink(response),
      invalidatesTags: (_result, _error, { propertyId }) => [
        { type: 'PropertyYoutubeLink', id: String(propertyId) },
        { type: 'Property', id: String(propertyId) }
      ]
    }),
    removeYoutubeLink: builder.mutation<void, RemoveYoutubeLinkArg>({
      query: ({ propertyId, linkId }) => ({
        url: `properties/${propertyId}/youtube-links/${linkId}`,
        method: 'DELETE'
      }),
      invalidatesTags: (_result, _error, { propertyId }) => [
        { type: 'PropertyYoutubeLink', id: String(propertyId) },
        { type: 'Property', id: String(propertyId) }
      ]
    }),

    /* `/api/seller/properties/*` — the seller's own read model for one property. */
    getSellerProperty: builder.query<PropertyDetailResponse, PropertyIdArg>({
      query: ({ id }) => ({ url: `seller/properties/${id}` }),
      transformResponse: (response: unknown) => normalizePropertyDetail(response),
      providesTags: (_result, _error, { id }) => [
        { type: 'Property', id: String(id) },
        { type: 'PropertyDocument', id: String(id) }
      ]
    }),
    getSellerPropertyDocuments: builder.query<PropertyDocumentRow[], PropertyIdArg>({
      query: ({ id }) => ({ url: `seller/properties/${id}/documents` }),
      transformResponse: (response: unknown) => normalizeDocuments(response),
      providesTags: (_result, _error, { id }) => [{ type: 'PropertyDocument', id: String(id) }]
    }),
    getSellerTimeline: builder.query<PropertyTimelineResult, PropertyScopedArg>({
      query: ({ propertyId }) => ({ url: `seller/properties/${propertyId}/timeline` }),
      transformResponse: (response: unknown) => normalizeTimeline(response),
      providesTags: (_result, _error, { propertyId }) => [
        { type: 'PropertyTimeline', id: String(propertyId) }
      ]
    }),
    getSellerActivityLog: builder.query<ActivityLogEntry[], PropertyScopedArg>({
      query: ({ propertyId }) => ({ url: `seller/properties/${propertyId}/activity` }),
      transformResponse: (response: unknown) => normalizeActivity(response),
      providesTags: (_result, _error, { propertyId }) => [
        { type: 'PropertyActivity', id: String(propertyId) }
      ]
    }),
    /**
     * A re-upload changes the document, the property's derived status, its timeline and its
     * activity log in one write — the old page refetched all four together, so all four
     * tags are invalidated here.
     */
    reuploadSellerDocument: builder.mutation<ReuploadedSellerDocument, ReuploadSellerDocumentArg>({
      query: ({ propertyId, docId, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: `seller/properties/${propertyId}/documents/${docId}/reupload`,
          method: 'POST',
          body: formData
        };
      },
      transformResponse: (response: unknown) => normalizeReuploadedDocument(response),
      invalidatesTags: (_result, _error, { propertyId }) => [
        { type: 'PropertyDocument', id: String(propertyId) },
        { type: 'Property', id: String(propertyId) },
        { type: 'PropertyTimeline', id: String(propertyId) },
        { type: 'PropertyActivity', id: String(propertyId) }
      ]
    })
  })
});

export const {
  useListPropertiesQuery,
  useGetPropertyQuery,
  useLazyGetPropertyQuery,
  useCreatePropertyMutation,
  useUpdatePropertyMutation,
  useDeletePropertyMutation,
  useUploadPropertyDocumentMutation,
  useRemovePropertyDocumentMutation,
  useUploadPropertyPhotosMutation,
  useRemovePropertyPhotoMutation,
  useUploadPropertyVideosMutation,
  useRemovePropertyVideoMutation,
  useAddYoutubeLinkMutation,
  useRemoveYoutubeLinkMutation,
  useGetSellerPropertyQuery,
  useGetSellerPropertyDocumentsQuery,
  useGetSellerTimelineQuery,
  useGetSellerActivityLogQuery,
  useReuploadSellerDocumentMutation
} = propertyApi;
